/**
 * Format ISO date string to localized short date (e.g., "Jan 15, 2024")
 */
export function formatDate(isoString: string): string {
  const date = new Date(isoString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Format ISO date string to relative time (e.g., "2m ago", "3h ago", "5d ago")
 */
export function formatRelativeTime(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return dateStr
  }
}

/**
 * Capitalize first letter of a string
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Format credits with 2 decimal places
 * @param credits - Credit value (stored as integer, displayed with 2 decimals)
 * @param includeSign - Whether to include +/- sign prefix
 * @param includeSuffix - Whether to include " cr" suffix
 */
export function formatCredits(
  credits: bigint | number,
  options: {
    includeSign?: boolean
    includeSuffix?: boolean
    includeDecimals?: boolean
  } = {}
): string {
  const {
    includeSign = false,
    includeSuffix = true,
    includeDecimals = true,
  } = options
  const sign = includeSign && credits > 0 ? '+' : ''
  const decimals = includeDecimals ? 2 : 0
  const formatted = (
    typeof credits === 'bigint' ? Number(credits) / 100 : credits
  ).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
  return includeSuffix ? `${sign}${formatted} cr` : `${sign}${formatted}`
}

/**
 * Format billing method for display
 * Maps API values to user-friendly labels
 */
export function formatBillingMethod(method: string): string {
  switch (method) {
    case 'free':
      return 'Personal'
    case 'prepaid':
      return 'Prepaid'
    case 'postpaid':
      return 'Postpaid'
    default:
      return capitalize(method)
  }
}

/**
 * Format bytes to human-readable string (B, KB, MB, GB, TB)
 */
export function formatBytes(bytes: number | bigint | undefined | null) {
  const [value, unit] = formatBytesSplit(bytes)
  return `${value} ${unit}`
}

export function formatBytesSplit(
  bytes: number | bigint | undefined | null
): [string, string] {
  if (bytes == null) return ['-', '']

  const isBigInt = typeof bytes === 'bigint'
  const base = 1024
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB']

  if (!isBigInt) {
    if (!Number.isFinite(bytes)) return ['-', '']
    let value = bytes
    let i = 0

    while (value >= base && i < units.length - 1) {
      value /= base
      i++
    }

    return i === 0
      ? [value.toLocaleString(), units[i]]
      : [value.toFixed(2), units[i]]
  }

  // bigint path (no decimals)
  let value = bytes
  let i = 0n

  while (value >= BigInt(base) && Number(i) < units.length - 1) {
    value /= BigInt(base)
    i++
  }

  return [value.toLocaleString(), units[Number(i)]]
}

/**
 * Safely stringify JSON, converting BigInt values to strings.
 * Use this instead of JSON.stringify when the value may contain BigInt.
 *
 * @param value - The value to stringify
 * @param space - Number of spaces for indentation (default: 2)
 * @returns JSON string with BigInt values converted to strings
 */
export function safeJsonStringify(value: unknown, space: number = 2): string {
  return JSON.stringify(
    value,
    (_key, val) => (typeof val === 'bigint' ? val.toString() : val),
    space
  )
}
